import React, { useState, useEffect } from 'react';
import { groupBookingsService } from '../../../services/groupBookingsService';
import GroupForm from './GroupForm';
import GroupDetails from './GroupDetails';
import './GroupsManagement.css';

const GroupsManagement = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await groupBookingsService.getAll();
      setGroups(data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupCreate = async (groupData) => {
    try {
      await groupBookingsService.create(groupData);
      fetchGroups();
      setShowForm(false);
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Failed to create group booking. Please try again.');
    }
  };

  const handleViewDetails = (group) => {
    setSelectedGroup(group);
    setShowDetails(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'status-badge active',
      pending: 'status-badge pending',
      completed: 'status-badge completed',
      cancelled: 'status-badge cancelled'
    };
    return statusClasses[status] || 'status-badge';
  };

  // Filter groups based on status and search term
  const filteredGroups = groups.filter(group => {
    // Status filter
    if (filter !== 'all' && group.status !== filter) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        group.title?.toLowerCase().includes(search) ||
        group.destination?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  // Calculate statistics
  const stats = {
    total: groups.length,
    active: groups.filter(g => g.status === 'active').length,
    upcoming: groups.filter(g => {
      const startDate = new Date(g.start_date);
      return startDate > new Date() && g.status === 'active';
    }).length,
    totalRevenue: groups.reduce((sum, g) => sum + parseFloat(g.total_revenue || 0), 0)
  };

  if (loading) {
    return (
      <div className="groups-loading">
        <div className="loading-spinner"></div>
        <p>Loading group bookings...</p>
      </div>
    );
  }

  return (
    <div className="groups-management">
      {/* Header */}
      <div className="groups-header">
        <h1>Group Bookings</h1>
        <button 
          className="create-group-btn"
          onClick={() => setShowForm(true)}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Group Booking
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="groups-stats">
        <div className="stat-card">
          <h3>Total Groups</h3>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card active">
          <h3>Active Groups</h3>
          <div className="stat-value">{stats.active}</div>
        </div>
        <div className="stat-card upcoming">
          <h3>Upcoming Groups</h3>
          <div className="stat-value">{stats.upcoming}</div>
        </div>
        <div className="stat-card revenue">
          <h3>Total Revenue</h3>
          <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="groups-filters mb-4">
        <input
          type="text"
          placeholder="Search by group name or destination..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ marginRight: '1rem' }}
        />
        
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Groups Table */}
      <div className="groups-table-container">
        {filteredGroups.length === 0 ? (
          <div className="empty-state">
            <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3>No group bookings found</h3>
            <p>Create your first group booking to get started</p>
          </div>
        ) : (
          <table className="groups-table">
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Destination</th>
                <th>Travel Dates</th>
                <th>Status</th>
                <th>Bookings</th>
                <th>Revenue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map(group => (
                <tr key={group.id}>
                  <td>
                    <div>
                      <div className="group-title">{group.title}</div>
                      <div className="group-destination">{group.component_type}</div>
                    </div>
                  </td>
                  <td>{group.destination}</td>
                  <td>
                    {formatDate(group.start_date)} - {formatDate(group.end_date)}
                  </td>
                  <td>
                    <span className={getStatusBadge(group.status)}>
                      {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="bookings-count">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {group.participant_count || 0}
                    </div>
                  </td>
                  <td className="revenue-amount">
                    {formatCurrency(group.total_revenue)}
                  </td>
                  <td>
                    <div className="group-actions">
                      <button
                        className="action-btn"
                        onClick={() => handleViewDetails(group)}
                        title="View Details"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => window.open(group.public_url, '_blank')}
                        title="View Public Page"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => {
                          if (navigator.clipboard) {
                            navigator.clipboard.writeText(group.public_url);
                            alert('Public URL copied to clipboard!');
                          }
                        }}
                        title="Copy Public URL"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Group Form Modal */}
      {showForm && (
        <div className="group-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="group-modal" onClick={(e) => e.stopPropagation()}>
            <GroupForm 
              onClose={() => setShowForm(false)}
              onSubmit={handleGroupCreate}
            />
          </div>
        </div>
      )}

      {/* Group Details Modal */}
      {showDetails && selectedGroup && (
        <div className="group-modal-overlay" onClick={() => setShowDetails(false)}>
          <div className="group-modal" onClick={(e) => e.stopPropagation()}>
            <GroupDetails 
              group={selectedGroup}
              onClose={() => setShowDetails(false)}
              onUpdate={fetchGroups}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsManagement;
